import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';
import { Html5Qrcode } from 'html5-qrcode';
import { addCrewMember, getVessel } from '../../../../api/VesselDetails/addVesselApi';
import Swal from 'sweetalert2';
import { FaEye, FaEdit, FaUserPlus, FaQrcode } from 'react-icons/fa';

const CrewMember = ({ isOpen, toggle, vesselId, vesselName, onCrewAdded }) => {
  const [crewList, setCrewList] = useState([
    { crewId: '', name: '' },
    { crewId: '', name: '' },
    { crewId: '', name: '' },
    { crewId: '', name: '' },
    { crewId: '', name: '' },
    { crewId: '', name: '' },
  ]); // Store both crewId and name
  const [scanResult, setScanResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [isScanning, setIsScanning] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('Checking camera...');
  const [isManualInput, setIsManualInput] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [currentCrewIndex, setCurrentCrewIndex] = useState(null);
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const isScannerRunning = useRef(false);

  // Stop scanner safely
  const stopScanner = async () => {
    if (scannerRef.current && isScannerRunning.current) {
      try {
        await scannerRef.current.stop();
        console.log('Scanner stopped successfully');
      } catch (err) {
        console.error('Error stopping scanner:', err.message);
      } finally {
        isScannerRunning.current = false;
        scannerRef.current = null;
      }
    }
  };

  // Fetch vessel data to pre-populate crewList
  useEffect(() => {
    if (isOpen) {
      const fetchVesselData = async () => {
        try {
          const vessels = await getVessel(vesselId);
          console.log('Fetched vessels:', vessels.data);
          const vessel = vessels.data; // Assuming the API returns a single vessel object
          if (vessel) {
            const newCrewList = [
              { crewId: vessel.Crew1 || '', name: vessel.Crew1Name || '' },
              { crewId: vessel.Crew2 || '', name: vessel.Crew2Name || '' },
              { crewId: vessel.Crew3 || '', name: vessel.Crew3Name || '' },
              { crewId: vessel.Crew4 || '', name: vessel.Crew4Name || '' },
              { crewId: vessel.Crew5 || '', name: vessel.Crew5Name || '' },
              { crewId: vessel.Crew6 || '', name: vessel.Crew6Name || '' },
            ];
            setCrewList(newCrewList);
          }
        } catch (error) {
          console.error('Error fetching vessel data:', error);
          setErrors({
            fetch: 'Failed to load existing crew data. Please try again.',
          });
        }
      };
      fetchVesselData();
    }
  }, [isOpen, vesselId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCrewList([
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
      ]);
      setScanResult(null);
      setErrors({});
      setIsScanning(false);
      setIsManualInput(true);
      setCameraStatus('Checking camera...');
      setSelectedCameraId('');
      setCurrentCrewIndex(null);
    }
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  // Initialize cameras
  useEffect(() => {
    if (!isOpen || !isScanning || isManualInput) return;

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        console.log('Camera access granted');
        setCameraStatus('Camera ready');
        stream.getTracks().forEach((track) => track.stop());

        Html5Qrcode.getCameras()
          .then((devices) => {
            console.log('Available cameras:', devices);
            setCameras(devices);
            if (devices.length > 0) {
              setSelectedCameraId(devices[0].id);
            }
          })
          .catch((err) => {
            console.error('Error listing cameras:', err);
            setErrors({ scan: `Failed to list cameras: ${err.message}` });
            setCameraStatus('No cameras found');
          });
      })
      .catch((err) => {
        console.error('Camera access error:', err.name, err.message);
        setErrors({
          scan: `Camera access denied: ${err.name}. Please allow camera permissions.`,
        });
        setCameraStatus('Camera access denied');
      });
  }, [isOpen, isScanning, isManualInput]);

  // Start scanner when camera is selected
  useEffect(() => {
    if (
      !isScanning ||
      isManualInput ||
      !selectedCameraId ||
      !scannerContainerRef.current
    )
      return;

    const startScanner = async () => {
      await stopScanner();

      scannerRef.current = new Html5Qrcode('qr-scanner');
      try {
        await scannerRef.current.start(
          selectedCameraId,
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            disableFlip: false,
          },
          (decodedText) => {
            console.log('QR code data:', decodedText);
            handleScan(decodedText);
          },
          (errorMessage) => {
            console.warn('QR scan error:', errorMessage);
          }
        );
        isScannerRunning.current = true;
        console.log('Scanner started successfully');
      } catch (err) {
        console.error('Error starting scanner:', err.message);
        if (err.message.includes('play')) {
          setErrors({
            scan: 'Camera failed to start. Please try again or switch cameras.',
          });
        } else {
          setErrors({ scan: `Failed to start scanner: ${err.message}` });
        }
      }
    };

    startScanner();

    return () => {
      stopScanner();
    };
  }, [selectedCameraId, isScanning, isManualInput]);

  // Handle QR code scan
  const handleScan = async (data) => {
    if (data && currentCrewIndex !== null) {
      try {
        const [crewId, name] = data.split(',').map((part) => part.trim()); // Extract crewId and name
        console.log('Parsed QR code:', { crewId, name });

        if (crewId) {
          // Check for duplicate crewId
          if (
            crewList.some(
              (crew, index) =>
                crew.crewId === crewId && index !== currentCrewIndex
            )
          ) {
            setErrors({
              scan: `Crew ID ${crewId} is already used in another slot.`,
            });
            return;
          }
          const newCrewList = [...crewList];
          newCrewList[currentCrewIndex] = { crewId, name: name || '' };
          setCrewList(newCrewList);
          await stopScanner();
          setIsScanning(false);
          setScanResult({ crewId, name });
          setErrors({});
        } else {
          setErrors({ scan: 'Invalid QR code: Must contain a valid crewId' });
        }
      } catch (error) {
        console.error('QR parse error:', error);
        setErrors({
          scan: 'Invalid QR code format. Expected format: crewId,username (e.g., 45dff4ef5d4f5df,John)',
        });
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (index, value) => {
    const newCrewList = [...crewList];
    newCrewList[index] = { ...newCrewList[index], crewId: value };
    setCrewList(newCrewList);
    setErrors({ ...errors, [`crewId${index}`]: '' });
  };

  // Handle file upload for QR code
  const handleFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const scanner = new Html5Qrcode('qr-scanner');
      scanner
        .scanFile(file, true)
        .then((decodedText) => {
          console.log('QR code from image:', decodedText);
          const [crewId, name] = decodedText
            .split(',')
            .map((part) => part.trim());
          if (crewId) {
            if (
              crewList.some((crew, i) => crew.crewId === crewId && i !== index)
            ) {
              setErrors({
                scan: `Crew ID ${crewId} is already used in another slot.`,
              });
              return;
            }
            const newCrewList = [...crewList];
            newCrewList[index] = { crewId, name: name || '' };
            setCrewList(newCrewList);
            setErrors({});
          } else {
            setErrors({ scan: 'Invalid QR code: Must contain a valid crewId' });
          }
        })
        .catch((err) => {
          console.error('Error scanning image:', err);
          setErrors({
            scan: 'No QR code found in uploaded image or invalid image.',
          });
        });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const validCrew = crewList.filter((crew) => crew.crewId);

    if (validCrew.length === 0) {
      newErrors.submit = 'At least one crew member must be added.';
    }

    validCrew.forEach((crew, index) => {
      if (!crew.crewId) {
        newErrors[`crewId${index}`] = `Crew ID is required for Crew ${
          index + 1
        }`;
      }
      // Check for duplicate crewId
      if (
        crew.crewId &&
        validCrew.some((c, i) => c.crewId === crew.crewId && i !== index)
      ) {
        newErrors[
          `crewId${index}`
        ] = `Crew ID ${crew.crewId} is already used in another slot.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const crewData = {
        IMULA_Number: vesselId,
        Crew1: '',
        Crew2: '',
        Crew3: '',
        Crew4: '',
        Crew5: '',
        Crew6: '',
        Crew1Name: '',
        Crew2Name: '',
        Crew3Name: '',
        Crew4Name: '',
        Crew5Name: '',
        Crew6Name: '',
      };

      // Assign valid crew members and names to slots
      const validCrew = crewList.filter((crew) => crew.crewId);
      validCrew.forEach((crew, index) => {
        crewData[`Crew${index + 1}`] = crew.crewId;
        crewData[`Crew${index + 1}Name`] = crew.name;
      });

      console.log('Submitting crew data:', crewData);
      const response = await addCrewMember(crewData);
      console.log('Crew added successfully:', response);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Crew members added successfully!',
        confirmButtonColor: '#3085d6',
      });

      onCrewAdded();
      await stopScanner();
      setCrewList([
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
        { crewId: '', name: '' },
      ]);
      setScanResult(null);
      setErrors({});
      setIsScanning(false);
      setIsManualInput(true);
      toggle();
    } catch (error) {
      console.error('Error adding crew members:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add crew members. Please try again.',
        confirmButtonColor: '#3085d6',
      });
      setErrors({ submit: 'Failed to add crew members. Please try again.' });
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    await stopScanner();
    setCrewList([
      { crewId: '', name: '' },
      { crewId: '', name: '' },
      { crewId: '', name: '' },
      { crewId: '', name: '' },
      { crewId: '', name: '' },
      { crewId: '', name: '' },
    ]);
    setScanResult(null);
    setErrors({});
    setIsScanning(false);
    setIsManualInput(true);
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      size="md"
    >
      <ModalHeader toggle={handleCancel}>
        Add Crew Members to {vesselName}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          {isScanning && !isManualInput && (
            <FormGroup>
              <Label>Scan QR Code for Crew {currentCrewIndex + 1}</Label>
              <div
                id="qr-scanner"
                ref={scannerContainerRef}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  margin: '0 auto',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              />
              <div className="text-muted text-center mt-2">{cameraStatus}</div>
              {errors.scan && (
                <div className="text-danger mt-2">{errors.scan}</div>
              )}
              {cameras.length > 1 && (
                <FormGroup className="mt-2">
                  <Label for="cameraSelect">Select Camera</Label>
                  <Input
                    type="select"
                    id="cameraSelect"
                    value={selectedCameraId}
                    onChange={(e) => setSelectedCameraId(e.target.value)}
                  >
                    {cameras.map((camera) => (
                      <option
                        key={camera.id}
                        value={camera.id}
                      >
                        {camera.label || `Camera ${camera.id}`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              )}
              <div className="d-flex justify-content-center mt-2 flex-wrap">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setIsScanning(false);
                    setIsManualInput(true);
                    setCurrentCrewIndex(null);
                  }}
                  className="mb-1"
                >
                  Enter Manually
                </Button>
              </div>
            </FormGroup>
          )}
          {(!isScanning || isManualInput) && (
            <>
              {crewList.map((crew, index) => (
                <div
                  key={index}
                  className=""
                >
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="mb-0 me-2">Crew Member {index + 1}</h6>
                    <span className="text-muted">
                      (Fisherman ID{crew.name ? `: ${crew.name}` : ''})
                    </span>
                  </div>
                  <div className="d-flex align-items-end mb-4 gap-2">
                    <Input
                      type="text"
                      name={`crewId${index}`}
                      id={`crewId${index}`}
                      value={crew.crewId}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      invalid={!!errors[`crewId${index}`]}
                    />
                    <FormFeedback>{errors[`crewId${index}`]}</FormFeedback>
                    <Button
                      color="primary"
                      size="lg"
                      onClick={() => {
                        setCurrentCrewIndex(index);
                        setIsScanning(true);
                        setIsManualInput(false);
                        setErrors({});
                      }}
                      className="mb-0 d-flex align-items-center gap-1"
                    >
                      <FaQrcode />
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
          {errors.submit && (
            <div className="text-danger mb-3">{errors.submit}</div>
          )}
          {errors.fetch && (
            <div className="text-danger mb-3">{errors.fetch}</div>
          )}
          <ModalFooter>
            <Button
              color="secondary"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
            >
              Add Crew Members
            </Button>
          </ModalFooter>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default CrewMember;